// LocalStoreFeatured.tsx
import React, { useEffect, useState } from 'react';
import './LocalStoreFeatured.scss';
import { http, localstorage, useNavigationService } from '../fx-core';
import StorefrontIcon from '@material-ui/icons/Storefront';
import AccessTimeIcon from '@material-ui/icons/AccessTime';

interface FeaturedStore {
  id: number;
  slotId: number;
  storeName: string;
  logoUrl: string | null;
  isLive: boolean;
  status?: 'ComingSoon' | 'Live' | 'ClosingSoon';
  slotStart: string;
  slotEnd: string;
}

interface Props {
  // Pass this explicitly whenever the calling page already knows which
  // community it's showing (e.g. CustomerDashboard). Falls back to
  // localstorage only when no prop is given, so this component still
  // works in places that haven't been updated to pass it in.
  communityId?: number | string;
}

// Renders "Opens in 2h 14m" / "Opens tomorrow, 10:00 AM" style countdowns from
// an IST-formatted slotStart the server already converted for us.
const formatCountdown = (slotStartIso: string): string => {
  const start = new Date(slotStartIso).getTime();
  const now = Date.now();
  const diffMs = start - now;

  if (diffMs <= 0) return 'Starting soon';

  const diffMins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;

  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `Opens in ${days}d ${hours % 24}h`;
  }
  if (hours > 0) return `Opens in ${hours}h ${mins}m`;
  return `Opens in ${mins}m`;
};

const LocalStoreFeatured = ({ communityId: communityIdProp }: Props) => {
  const { navigate } = useNavigationService();
  const [stores, setStores] = useState<FeaturedStore[]>([]);
  const [loading, setLoading] = useState(true);
  const [, forceTick] = useState(0);

  const getFeaturedStores = () => {
    const communityId = communityIdProp || localstorage.getItem("communityId");
    if (!communityId) {
      setLoading(false);
      setStores([]);
      return;
    }
    setLoading(true);
    http.get({ url: `localstore/community/${communityId}/featured` })
      .then((res: any) => setStores(res.data?.stores || []))
      .catch(() => setStores([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getFeaturedStores();
    // Re-fetch whenever the calling page hands us a different community
    // (e.g. navigating between community dashboards without a full remount).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityIdProp]);

  // Re-render every 60s so live countdowns and the isLive flip stay fresh
  // without a full refetch — the isLive flag itself still comes from the
  // server on next load, this just keeps the displayed countdown ticking.
  useEffect(() => {
    const interval = setInterval(() => forceTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

    const navToStore = (store: FeaturedStore) => {
    if (!store.isLive) return;
    // Static link — always resolves to whichever stall is live at the time
    // it's opened, rather than baking this specific booking/slot into the URL.
    navigate({
      targetRoute: '/localstore',
      targetTitle: store.storeName || 'Local Store',
      targetData: {},
      sourceState: {},
      eventSource: '',
    });
  };

  if (loading || stores.length === 0) return null;

  return (
    <div className="local-store-featured-ui">
      <div className="lsf-header">
        <StorefrontIcon fontSize="small" />
        <span className="lsf-title">Local Stores</span>
      </div>

      <div className="lsf-scroll">
        {stores.map((store) => (
          <div
            key={store.id}
            className={`lsf-card${store.isLive ? ' lsf-card-live' : ' lsf-card-disabled'}`}
            onClick={() => navToStore(store)}
            role="button"
            aria-disabled={!store.isLive}
          >
            <div className="lsf-logo-wrap">
              {store.logoUrl ? (
                <img src={store.logoUrl} alt={store.storeName} className="lsf-logo" />
              ) : (
                <div className="lsf-logo-placeholder"><StorefrontIcon fontSize="small" /></div>
              )}
              {!store.isLive && <div className="lsf-logo-scrim" />}
            </div>

            <div className="lsf-name">{store.storeName || 'Local Store'}</div>

            {store.status === 'ClosingSoon' ? (
              <div className="lsf-status lsf-status-closing">
                <span className="lsf-live-dot" />
                Closing soon
              </div>
            ) : store.isLive ? (
              <div className="lsf-status lsf-status-live">
                <span className="lsf-live-dot" />
                Open now
              </div>
            ) : (
              <div className="lsf-status lsf-status-countdown">
                <AccessTimeIcon style={{ fontSize: 12 }} />
                {formatCountdown(store.slotStart)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LocalStoreFeatured;